import type { SavingsListQuery } from '@/generated/gql/graphql';

export type SavingTotalsRow = Pick<
  SavingsListQuery['savings'][number],
  'baselinePrice' | 'baselineQuantity' | 'originalPriceQuantity' | 'newPrice' | 'newQuantity' | 'newPriceQuantity'
>;

const toNumber = (value: unknown): number | null => {
  if (value == null) return null;
  const normalized = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

// A null or zero price unit (Preiseinheit) means the price applies per single unit.
const priceUnitDivisor = (value: number | null): number => (value == null || value === 0 ? 1 : value);

export const calculateBaseTotal = (row: SavingTotalsRow): number | null => {
  const price = toNumber(row.baselinePrice);
  const quantity = toNumber(row.newQuantity);

  if (price == null || quantity == null) {
    return null;
  }

  return (price * quantity) / priceUnitDivisor(toNumber(row.originalPriceQuantity));
};

export const calculateNegotiatedTotal = (row: SavingTotalsRow): number | null => {
  const price = toNumber(row.newPrice);
  const quantity = toNumber(row.newQuantity);

  if (price == null || quantity == null) {
    return null;
  }

  return (price * quantity) / priceUnitDivisor(toNumber(row.newPriceQuantity));
};

export const calculateSavingTotal = (row: SavingTotalsRow): number | null => {
  const base = calculateBaseTotal(row);
  const negotiated = calculateNegotiatedTotal(row);

  if (base == null || negotiated == null) {
    return null;
  }

  return base - negotiated;
};

// Returns the saving as a 0-1 ratio so the consumer can apply locale-aware
// percent formatting (Intl `style: 'percent'` multiplies by 100).
export const calculateSavingRatio = (row: SavingTotalsRow): number | null => {
  const base = calculateBaseTotal(row);
  const saving = calculateSavingTotal(row);

  if (base == null || base === 0 || saving == null) {
    return null;
  }

  return saving / base;
};
